import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiEye,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiDollarSign,
  FiUsers,
  FiCalendar,
  FiImage,
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiTruck,
  FiMapPin,
} from "react-icons/fi";
import { mealPlansAPI } from "../../services/api";
import toast from "react-hot-toast";

const MealPlanManagementContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem 0;
`;

const MealPlanManagementContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const PageDescription = styled.div`
  margin-bottom: 2rem;
  text-align: center;

  p {
    color: #666;
    font-size: 1.125rem;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: 2px solid #2d5016;
  background: ${(props) => (props.$primary ? "#2d5016" : "white")};
  color: ${(props) => (props.$primary ? "white" : "#2d5016")};
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) => (props.$primary ? "#1a3a0e" : "#f8f9fa")};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const FiltersSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: end;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 150px;

  label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #333;
  }

  input,
  select {
    padding: 0.75rem;
    border: 2px solid #e0e0e0;
    border-radius: 6px;
    font-size: 0.875rem;
    background: white;

    &:focus {
      outline: none;
      border-color: #2d5016;
    }
  }
`;

const SearchBar = styled.div`
  position: relative;
  flex: 1;
  min-width: 300px;

  input {
    width: 100%;
    padding-left: 2.5rem;
  }

  .search-icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #666;
    font-size: 1.125rem;
  }
`;

const MealPlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const MealPlanCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-4px);
  }
`;

const MealPlanImage = styled.div`
  height: 200px;
  background: linear-gradient(135deg, #2d5016, #4a7c59);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 3rem;
  position: relative;

  .overlay {
    position: absolute;
    top: 1rem;
    right: 1rem;
    display: flex;
    gap: 0.5rem;
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;

  &.active {
    background: #d4edda;
    color: #155724;
  }

  &.inactive {
    background: #f8d7da;
    color: #721c24;
  }

  &.pending {
    background: #fff3cd;
    color: #856404;
  }
`;

const ActionButton = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const MealPlanContent = styled.div`
  padding: 1.5rem;

  .fulfillment-options {
    margin-bottom: 1rem;
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .fulfillment-badge {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    .icon {
      font-size: 0.8rem;
    }

    &.both {
      background: linear-gradient(135deg, #2d5016, #4a7c59);
      color: white;
    }

    &.delivery-only {
      background: #e8f5e8;
      color: #2d5016;
      border: 1px solid #2d5016;
    }

    &.pickup-only {
      background: #fff3e0;
      color: #e65100;
      border: 1px solid #e65100;
    }
  }

  .price-section {
    margin-bottom: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #e0e0e0;

    .pricing-options {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .price-option {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    .price-label {
      font-size: 0.875rem;
      font-weight: 600;
      color: #2d5016;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .price {
      font-size: 1.25rem;
      font-weight: 700;
      color: #2d5016;
    }

    .price-breakdown {
      font-size: 0.75rem;
      color: #666;
      font-style: italic;
    }

    .price-note {
      font-size: 0.75rem;
      color: #666;
    }

    .price-period {
      color: #666;
      font-size: 0.875rem;
    }
  }
`;

const MealPlanHeader = styled.div`
  margin-bottom: 1rem;

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2d5016;
    margin: 0 0 0.5rem 0;
  }

  p {
    color: #666;
    font-size: 0.875rem;
    margin: 0;
    line-height: 1.4;
  }
`;

const MealPlanStats = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #666;

  .icon {
    color: #2d5016;
  }

  .value {
    font-weight: 600;
    color: #2d5016;
  }
`;

const MealPlanActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const ActionIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #e0e0e0;
  background: white;
  color: #666;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #2d5016;
    color: #2d5016;
  }

  &.delete:hover {
    border-color: #dc3545;
    color: #dc3545;
  }
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 1.125rem;
  color: #666;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;

  h3 {
    margin-bottom: 1rem;
    color: #333;
  }
`;

const MealPlanManagement = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  // Helper function to check if meal plan is active
  const isMealPlanActive = (mealPlan) => {
    return (
      mealPlan.is_active === true ||
      mealPlan.isActive === true ||
      mealPlan.is_active === "true" ||
      mealPlan.isActive === "true" ||
      mealPlan.is_active === 1 ||
      mealPlan.isActive === 1
    );
  };

  // Fetch meal plans data
  const {
    data: mealPlansData,
    isLoading,
    error,
    refetch,
  } = useQuery("vendor-meal-plans", () => mealPlansAPI.getVendorMealPlans(), {
    keepPreviousData: true,
    refetchOnWindowFocus: true,
    staleTime: 0, // Always consider data stale to ensure fresh data
  });

  const deleteMealPlanMutation = useMutation(
    (mealPlanId) => mealPlansAPI.deleteMealPlan(mealPlanId),
    {
      onSuccess: () => {
        toast.success("Meal plan deleted successfully!");
        queryClient.invalidateQueries("vendor-meal-plans");
      },
      onError: (error) => {
        console.error("Error deleting meal plan:", error);
        toast.error(
          error.response?.data?.message || "Failed to delete meal plan"
        );
      },
    }
  );

  const mealPlans = mealPlansData?.data?.data?.mealPlans || [];

  // Debug: Log meal plan data to see what we're receiving
  if (mealPlans.length > 0) {
    console.log("Sample meal plan data:", mealPlans[0]);
    console.log(
      "offers_delivery:",
      mealPlans[0].offers_delivery,
      typeof mealPlans[0].offers_delivery
    );
    console.log(
      "offersDelivery:",
      mealPlans[0].offersDelivery,
      typeof mealPlans[0].offersDelivery
    );
    console.log(
      "offers_pickup:",
      mealPlans[0].offers_pickup,
      typeof mealPlans[0].offers_pickup
    );
    console.log(
      "offersPickup:",
      mealPlans[0].offersPickup,
      typeof mealPlans[0].offersPickup
    );
    console.log("base_price:", mealPlans[0].base_price);
    console.log("basePrice:", mealPlans[0].basePrice);
    console.log("delivery_fee:", mealPlans[0].delivery_fee);
    console.log("deliveryFee:", mealPlans[0].deliveryFee);
  }

  // Filter meal plans based on search and filters
  const filteredMealPlans = mealPlans.filter((mealPlan) => {
    const matchesSearch =
      mealPlan.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mealPlan.description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      !statusFilter ||
      (statusFilter === "active" && mealPlan.is_active) ||
      (statusFilter === "inactive" && !mealPlan.is_active);

    const matchesType = !typeFilter || mealPlan.plan_type === typeFilter;

    return matchesSearch && matchesStatus && matchesType;
  });

  const displayMealPlans = filteredMealPlans;

  const statusOptions = [
    { value: "", label: "All Statuses" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "pending", label: "Pending Approval" },
  ];

  const typeOptions = [
    { value: "", label: "All Types" },
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
  ];

  const handleCreateMealPlan = () => {
    navigate("/vendor/meal-plans/create");
  };

  const handleEditMealPlan = (mealPlanId) => {
    navigate(`/vendor/meal-plans/${mealPlanId}/edit`);
  };

  const handleDeleteMealPlan = (mealPlanId) => {
    // Show a more elegant confirmation using toast
    const confirmDelete = () => {
      deleteMealPlanMutation.mutate(mealPlanId);
    };

    // Create a custom confirmation toast
    toast(
      (t) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <div style={{ fontWeight: "bold" }}>Delete Meal Plan</div>
          <div>
            Are you sure you want to delete this meal plan? This action cannot
            be undone.
          </div>
          <div
            style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}
          >
            <button
              onClick={() => {
                toast.dismiss(t.id);
              }}
              style={{
                padding: "5px 15px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                background: "white",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                confirmDelete();
              }}
              style={{
                padding: "5px 15px",
                border: "none",
                borderRadius: "4px",
                background: "#dc3545",
                color: "white",
                cursor: "pointer",
              }}
            >
              Delete
            </button>
          </div>
        </div>
      ),
      {
        duration: Infinity, // Keep the toast open until user clicks
        id: "delete-confirmation",
      }
    );
  };

  const handleViewMealPlan = (mealPlanId) => {
    navigate(`/vendor/meal-plans/${mealPlanId}`);
  };

  const toggleStatusMutation = useMutation(
    (mealPlanId) => mealPlansAPI.toggleMealPlanStatus(mealPlanId),
    {
      onSuccess: (response) => {
        const message =
          response?.data?.message || "Meal plan status updated successfully!";
        toast.success(message);
        queryClient.invalidateQueries("vendor-meal-plans");
      },
      onError: (error) => {
        console.error("Error updating meal plan status:", error);
        toast.error(
          error.response?.data?.message || "Failed to update meal plan status"
        );
      },
    }
  );

  const handleToggleStatus = (mealPlanId) => {
    toggleStatusMutation.mutate(mealPlanId);
  };

  if (isLoading) {
    return (
      <MealPlanManagementContainer>
        <MealPlanManagementContent>
          <LoadingState>
            <FiRefreshCw className="animate-spin" />
            Loading meal plans...
          </LoadingState>
        </MealPlanManagementContent>
      </MealPlanManagementContainer>
    );
  }

  if (error) {
    return (
      <MealPlanManagementContainer>
        <MealPlanManagementContent>
          <EmptyState>
            <h3>Error loading meal plans</h3>
            <p>{error.message}</p>
          </EmptyState>
        </MealPlanManagementContent>
      </MealPlanManagementContainer>
    );
  }

  return (
    <MealPlanManagementContainer>
      <MealPlanManagementContent>
        <PageDescription>
          <p>Create and manage your meal plans</p>
        </PageDescription>

        <HeaderActions>
          <Button onClick={() => refetch()}>
            <FiRefreshCw />
            Refresh
          </Button>
          <Button $primary onClick={handleCreateMealPlan}>
            <FiPlus />
            Add Meal Plan
          </Button>
        </HeaderActions>

        <FiltersSection>
          <FiltersRow>
            <SearchBar>
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search meal plans..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBar>

            <FilterGroup>
              <label>Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FilterGroup>

            <FilterGroup>
              <label>Type</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                {typeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FilterGroup>
          </FiltersRow>
        </FiltersSection>

        {displayMealPlans.length === 0 ? (
          <EmptyState>
            <h3>No meal plans found</h3>
            <p>Create your first meal plan to get started.</p>
            <Button
              $primary
              onClick={handleCreateMealPlan}
              style={{ marginTop: "1rem" }}
            >
              <FiPlus />
              Create Meal Plan
            </Button>
          </EmptyState>
        ) : (
          <MealPlansGrid>
            {displayMealPlans.map((mealPlan, index) => (
              <MealPlanCard
                key={mealPlan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <MealPlanImage>
                  {mealPlan.image_url || mealPlan.imageUrl ? (
                    <img
                      src={mealPlan.image_url || mealPlan.imageUrl}
                      alt={mealPlan.title}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <FiImage />
                  )}
                  <div className="overlay">
                    <StatusBadge
                      className={
                        isMealPlanActive(mealPlan) ? "active" : "inactive"
                      }
                    >
                      {isMealPlanActive(mealPlan) ? "Active" : "Inactive"}
                    </StatusBadge>
                    <ActionButton
                      onClick={() => handleToggleStatus(mealPlan.id)}
                      title={
                        isMealPlanActive(mealPlan) ? "Deactivate" : "Activate"
                      }
                      disabled={toggleStatusMutation.isLoading}
                    >
                      {isMealPlanActive(mealPlan) ? (
                        <FiXCircle />
                      ) : (
                        <FiCheckCircle />
                      )}
                    </ActionButton>
                  </div>
                </MealPlanImage>

                <MealPlanContent>
                  <MealPlanHeader>
                    <h3>{mealPlan.title}</h3>
                    <p>{mealPlan.description}</p>
                  </MealPlanHeader>

                  <MealPlanStats>
                    <StatItem>
                      <FiUsers className="icon" />
                      <span className="value">
                        {parseInt(mealPlan.current_subscribers) || 0}
                      </span>
                      <span>subscribers</span>
                    </StatItem>
                    <StatItem>
                      <FiCalendar className="icon" />
                      <span>{mealPlan.duration_days} days</span>
                    </StatItem>
                    <StatItem>
                      <FiUsers className="icon" />
                      <span>Max {mealPlan.max_subscribers}</span>
                    </StatItem>
                  </MealPlanStats>

                  <div className="fulfillment-options">
                    {(() => {
                      const offersDelivery =
                        mealPlan.offers_delivery === true ||
                        mealPlan.offers_delivery === "true" ||
                        mealPlan.offers_delivery === 1 ||
                        mealPlan.offersDelivery === true ||
                        mealPlan.offersDelivery === "true" ||
                        mealPlan.offersDelivery === 1;
                      const offersPickup =
                        mealPlan.offers_pickup === true ||
                        mealPlan.offers_pickup === "true" ||
                        mealPlan.offers_pickup === 1 ||
                        mealPlan.offersPickup === true ||
                        mealPlan.offersPickup === "true" ||
                        mealPlan.offersPickup === 1;

                      if (offersDelivery && offersPickup) {
                        return (
                          <div className="fulfillment-badge both">
                            <FiTruck className="icon" />
                            <span>Delivery & Pickup</span>
                          </div>
                        );
                      } else if (offersDelivery && !offersPickup) {
                        return (
                          <div className="fulfillment-badge delivery-only">
                            <FiTruck className="icon" />
                            <span>Delivery Only</span>
                          </div>
                        );
                      } else if (!offersDelivery && offersPickup) {
                        return (
                          <div className="fulfillment-badge pickup-only">
                            <FiMapPin className="icon" />
                            <span>Pickup Only</span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>

                  <div className="price-section">
                    <div className="pricing-options">
                      {(() => {
                        const offersDelivery =
                          mealPlan.offers_delivery === true ||
                          mealPlan.offers_delivery === "true" ||
                          mealPlan.offers_delivery === 1 ||
                          mealPlan.offersDelivery === true ||
                          mealPlan.offersDelivery === "true" ||
                          mealPlan.offersDelivery === 1;
                        const offersPickup =
                          mealPlan.offers_pickup === true ||
                          mealPlan.offers_pickup === "true" ||
                          mealPlan.offers_pickup === 1 ||
                          mealPlan.offersPickup === true ||
                          mealPlan.offersPickup === "true" ||
                          mealPlan.offersPickup === 1;
                        const basePrice = parseFloat(
                          mealPlan.base_price ||
                            mealPlan.basePrice ||
                            mealPlan.price ||
                            0
                        );
                        const deliveryFee = parseFloat(
                          mealPlan.delivery_fee || mealPlan.deliveryFee || 0
                        );

                        if (offersDelivery) {
                          return (
                            <div className="price-option">
                              <div className="price-label">Delivery</div>
                              <div className="price">
                                GYD $
                                {Math.round(
                                  basePrice + deliveryFee
                                ).toLocaleString()}
                              </div>
                              {deliveryFee > 0 && (
                                <div className="price-breakdown">
                                  Base: $
                                  {Math.round(basePrice).toLocaleString()} +
                                  Delivery: $
                                  {Math.round(deliveryFee).toLocaleString()}
                                </div>
                              )}
                            </div>
                          );
                        }
                        if (offersPickup) {
                          return (
                            <div className="price-option">
                              <div className="price-label">Pickup</div>
                              <div className="price">
                                GYD ${Math.round(basePrice).toLocaleString()}
                              </div>
                              <div className="price-note">
                                Pickup from{" "}
                                {mealPlan.pickup_address ||
                                  mealPlan.pickupAddress ||
                                  "vendor location"}
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div className="price-option">
                            <div className="price">
                              GYD $
                              {Math.round(
                                parseFloat(mealPlan.price || 0)
                              ).toLocaleString()}
                            </div>
                            <div className="price-period">
                              per {mealPlan.plan_type || mealPlan.planType}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  <MealPlanActions>
                    <ActionIconButton
                      onClick={() => handleViewMealPlan(mealPlan.id)}
                      title="View Details"
                    >
                      <FiEye />
                    </ActionIconButton>
                    <ActionIconButton
                      onClick={() => handleEditMealPlan(mealPlan.id)}
                      title="Edit Meal Plan"
                    >
                      <FiEdit />
                    </ActionIconButton>
                    <ActionIconButton
                      onClick={() => handleDeleteMealPlan(mealPlan.id)}
                      title="Delete Meal Plan"
                      className="delete"
                    >
                      <FiTrash2 />
                    </ActionIconButton>
                  </MealPlanActions>
                </MealPlanContent>
              </MealPlanCard>
            ))}
          </MealPlansGrid>
        )}
      </MealPlanManagementContent>
    </MealPlanManagementContainer>
  );
};

export default MealPlanManagement;
